import React, { useState, useId, useCallback } from 'react';
import { isValidElementType } from 'react-is';
import { clsx } from 'clsx';
import TextInputInnerVisualSlot from '../internal/components/TextInputInnerVisualSlot.js';
import { TextInputWrapper } from '../internal/components/TextInputWrapper.js';
import TextInputAction from '../internal/components/TextInputInnerAction.js';
import UnstyledTextInput from '../internal/components/UnstyledTextInput.js';
import VisuallyHidden from '../_VisuallyHidden.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useProvidedRefOrCreate } from '../hooks/useProvidedRefOrCreate.js';

// using forwardRef is important so that other components can autofocus the input
const TextInput = /*#__PURE__*/React.forwardRef(({
  icon: IconComponent,
  leadingVisual: LeadingVisual,
  trailingVisual: TrailingVisual,
  trailingAction,
  block,
  className,
  contrast,
  disabled,
  loading,
  loaderPosition = 'auto',
  loaderText = 'Loading',
  monospace,
  validationStatus,
  sx: sxProp,
  size: sizeProp,
  onFocus,
  onBlur,
  // start deprecated props
  width: widthProp,
  minWidth: minWidthProp,
  maxWidth: maxWidthProp,
  variant: variantProp,
  // end deprecated props
  type = 'text',
  required,
  ...inputProps
}, ref) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useProvidedRefOrCreate(ref);
  // this class is necessary to style FilterSearch, plz no touchy!
  const wrapperClasses = clsx(className, 'TextInput-wrapper');
  const showLeadingLoadingIndicator = loading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'));
  const showTrailingLoadingIndicator = loading && (loaderPosition === 'trailing' || Boolean(loaderPosition === 'auto' && !LeadingVisual));
  const focusInput = () => {
    var _inputRef$current;
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  };
  const leadingVisualId = useId();
  const trailingVisualId = useId();
  const loadingId = useId();
  const inputDescribedBy = clsx(inputProps['aria-describedby'], LeadingVisual && leadingVisualId, TrailingVisual && trailingVisualId, loading && loadingId) || undefined;
  const handleInputFocus = useCallback(e => {
    setIsInputFocused(true);
    onFocus && onFocus(e);
  }, [onFocus]);
  const handleInputBlur = useCallback(e_0 => {
    setIsInputFocused(false);
    onBlur && onBlur(e_0);
  }, [onBlur]);
  return /*#__PURE__*/jsxs(TextInputWrapper, {
    block: block,
    className: wrapperClasses,
    validationStatus: validationStatus,
    contrast: contrast,
    disabled: disabled,
    monospace: monospace,
    sx: sxProp,
    size: sizeProp,
    width: widthProp,
    minWidth: minWidthProp,
    maxWidth: maxWidthProp,
    variant: variantProp,
    hasLeadingVisual: Boolean(LeadingVisual || showLeadingLoadingIndicator),
    hasTrailingVisual: Boolean(TrailingVisual || showTrailingLoadingIndicator),
    hasTrailingAction: Boolean(trailingAction),
    isInputFocused: isInputFocused,
    onClick: focusInput,
    "aria-busy": Boolean(loading),
    children: [IconComponent && /*#__PURE__*/jsx(IconComponent, {
      className: "TextInput-icon"
    }), /*#__PURE__*/jsx(TextInputInnerVisualSlot, {
      visualPosition: "leading",
      showLoadingIndicator: showLeadingLoadingIndicator,
      hasLoadingIndicator: typeof loading === 'boolean',
      id: leadingVisualId,
      children: typeof LeadingVisual !== 'string' && isValidElementType(LeadingVisual) ? /*#__PURE__*/jsx(LeadingVisual, {}) : LeadingVisual
    }), /*#__PURE__*/jsx(UnstyledTextInput, {
      ref: inputRef,
      disabled: disabled,
      onFocus: handleInputFocus,
      onBlur: handleInputBlur,
      type: type,
      "aria-required": required,
      "aria-invalid": validationStatus === 'error' ? 'true' : undefined,
      ...inputProps,
      "aria-describedby": inputDescribedBy,
      "data-component": "input"
    }), loading && /*#__PURE__*/jsx(VisuallyHidden, {
      id: loadingId,
      children: loaderText
    }), /*#__PURE__*/jsx(TextInputInnerVisualSlot, {
      visualPosition: "trailing",
      showLoadingIndicator: showTrailingLoadingIndicator,
      hasLoadingIndicator: typeof loading === 'boolean',
      id: trailingVisualId,
      "data-testid": "text-input-trailing-visual",
      children: typeof TrailingVisual !== 'string' && isValidElementType(TrailingVisual) ? /*#__PURE__*/jsx(TrailingVisual, {}) : TrailingVisual
    }), trailingAction]
  });
});
TextInput.displayName = 'TextInput';
var TextInput$1 = Object.assign(TextInput, {
  Action: TextInputAction
});

export { TextInput$1 as default };
